class HzTabsComponent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  destroy() {
    if (!this.initialized) return;

    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.initialized = false;
  }

  initialize() {
    this.tabs = [...this.querySelectorAll('[role="tab"][data-hz-tab]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"][data-hz-tab-panel]')];

    if (!this.initialized) {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.initialized = true;
    }

    this.refresh();
  }

  refresh() {
    this.tabs = [...this.querySelectorAll('[role="tab"][data-hz-tab]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"][data-hz-tab-panel]')];

    if (this.tabs.length === 0) return;

    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.setActive(selectedTab.dataset.hzTab, false);
  }

  handleClick(event) {
    const tab = event.target.closest('[role="tab"][data-hz-tab]');
    if (!tab || !this.contains(tab)) return;

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.setActive(tab.dataset.hzTab, false);
  }

  handleKeydown(event) {
    const tab = event.target.closest('[role="tab"][data-hz-tab]');
    if (!tab || !this.contains(tab)) return;

    const currentIndex = this.tabs.indexOf(tab);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.setActive(tab.dataset.hzTab, true);
        return;
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setActive(this.tabs[nextIndex].dataset.hzTab, true);
  }

  setActive(tabId, moveFocus) {
    if (!this.tabs || !this.panels) return;

    const activeTab = this.tabs.find((tab) => tab.dataset.hzTab === tabId);
    if (!activeTab) return;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.hzTabPanel === tabId;
      panel.hidden = !isActive;
      panel.setAttribute('aria-hidden', String(!isActive));

      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });

    this.dataset.hzTabsReady = 'true';

    if (moveFocus) activeTab.focus();
  }

  handleEditorBlockSelect(target) {
    const panel = target?.closest?.('[data-hz-tab-panel]');
    if (panel && this.contains(panel)) {
      this.setActive(panel.dataset.hzTabPanel, false);
    }
  }
}

if (!customElements.get('hz-tabs-component')) {
  customElements.define('hz-tabs-component', HzTabsComponent);
}

const getHzTabs = (target) => {
  if (!target) return [];
  if (target.matches?.('hz-tabs-component')) return [target];
  return [...(target.querySelectorAll?.('hz-tabs-component') || [])];
};

const initializeHzTabs = (target) => {
  getHzTabs(target).forEach((tabs) => tabs.initialize());
};

const destroyHzTabs = (target) => {
  getHzTabs(target).forEach((tabs) => tabs.destroy());
};

if (!window.__hzTabsEditorEventsBound) {
  window.__hzTabsEditorEventsBound = true;

  document.addEventListener('shopify:section:load', (event) => initializeHzTabs(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyHzTabs(event.target));
  document.addEventListener('shopify:section:reorder', (event) => initializeHzTabs(event.target));
  document.addEventListener('shopify:section:select', (event) => initializeHzTabs(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const tabs = event.target?.closest?.('hz-tabs-component');
    tabs?.handleEditorBlockSelect(event.target);
  });
}
